import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FilePlus2, FileSignature, Trash2 } from 'lucide-react';
import { ContractCreateModal } from '../components/forms/ContractModal';
import { CarSelect, DriverSelect } from '../components/forms/EntitySelects';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { ConfirmDialog } from '../components/ui/Modal';
import { useContracts, useDeleteContract } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { contractDownloadUrl, errorMessage } from '../services/api';
import { toast } from '../store';
import type { Contract } from '../types';
import { driverName, formatCurrency, formatDate, toDateInput } from '../utils/helpers';
import { t } from '../i18n';

function ContractStatus({ contract }: { contract: Contract }) {
  const today = toDateInput();
  if (contract.assignment?.returned_at) return <Badge>{t('Vozidlo vrátené')}</Badge>;
  if (contract.date_from > today) return <Badge variant="info">{t('Pripravená')}</Badge>;
  if (contract.date_to < today) return <Badge>{t('Ukončená')}</Badge>;
  return <Badge variant="success">{t('Prebieha')}</Badge>;
}

export function Contracts() {
  const [filters, setFilters] = useUrlFilters({ search: '', car_id: '', driver_id: '', page: '1', page_size: '20' });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Contract | null>(null);
  const remove = useDeleteContract();

  const page = toInt(filters.page, 1)!;
  const pageSize = toInt(filters.page_size, 20)!;
  const { data, isLoading, isFetching } = useContracts({
    search: filters.search,
    car_id: toInt(filters.car_id),
    driver_id: toInt(filters.driver_id),
    page,
    page_size: pageSize,
  });

  const columns: Column<Contract>[] = [
    { key: 'number', header: t('Číslo'), className: 'whitespace-nowrap', render: (c) => <span className="font-mono font-medium">{c.number}</span> },
    { key: 'driver', header: t('Pracovník'), render: (c) => <span className="font-medium">{driverName(c.driver)}</span> },
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (c) => (
        <Link to={`/cars/${c.car_id}`} className="whitespace-nowrap hover:underline">
          <span className="font-mono font-medium text-primary-700">{c.car?.license_plate}</span>
          <span className="block text-xs text-secondary-500">
            {c.car?.brand} {c.car?.model}
          </span>
        </Link>
      ),
    },
    {
      key: 'period',
      header: t('Obdobie'),
      className: 'whitespace-nowrap',
      render: (c) => `${formatDate(c.date_from)} – ${formatDate(c.date_to)}`,
    },
    {
      key: 'price',
      header: t('Cena'),
      className: 'whitespace-nowrap text-sm',
      render: (c) => {
        if (c.daily_rate === null) return '–';
        const days = Math.round((Date.parse(c.date_to) - Date.parse(c.date_from)) / 86_400_000) + 1;
        return (
          <div>
            <p>{t('{rate} / deň', { rate: formatCurrency(c.daily_rate) })}</p>
            <p className="text-xs text-secondary-500">spolu {formatCurrency(c.daily_rate * days)}</p>
          </div>
        );
      },
    },
    { key: 'status', header: t('Stav'), render: (c) => <ContractStatus contract={c} /> },
    {
      key: 'created',
      header: t('Vystavil'),
      className: 'whitespace-nowrap',
      render: (c) => (
        <div className="text-sm">
          <p>{c.creator?.full_name || c.creator?.username || '–'}</p>
          <p className="text-xs text-secondary-500">{formatDate(c.created_at)}</p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (c) => (
        <>
          <a className="icon-btn" href={contractDownloadUrl(c.id)} title={t('Stiahnuť (Word)')} aria-label={t('Stiahnuť')}>
            <Download className="h-4 w-4" />
          </a>
          <button className="icon-btn-danger" onClick={() => setDeleting(c)} title={t('Zmazať')} aria-label={t('Zmazať')}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success(t('Zmluva {number} bola zmazaná', { number: deleting.number }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
    setDeleting(null);
  };

  return (
    <>
      <PageHeader
        title={t('Zmluvy')}
        subtitle={t('Zmluvy o nájme vozidiel – vygenerovaním zmluvy sa vozidlo zároveň pridelí pracovníkovi')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <FilePlus2 className="h-4 w-4" />  {t('Generovať zmluvu')}
          </Button>
        }
      />
      <Card padding="none">
        <FilterBar>
          <SearchInput value={filters.search} onChange={(search) => setFilters({ search })} placeholder={t('Číslo zmluvy, meno, ŠPZ…')} />
          <DriverSelect
            label={t('Pracovník')}
            onlyActive={false}
            wrapperClassName="sm:w-64"
            placeholder={t('Všetci pracovníci')}
            value={filters.driver_id}
            onChange={(e) => setFilters({ driver_id: e.target.value })}
          />
          <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-64" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value })} />
        </FilterBar>
        <DataTable
          data={data?.contracts}
          columns={columns}
          rowKey={(c) => c.id}
          isLoading={isLoading}
          isFetching={isFetching}
          emptyMessage={<EmptyState icon={<FileSignature className="h-10 w-10" />} title={t('Žiadne zmluvy nezodpovedajú filtru')} />}
          pagination={
            data && {
              page,
              pageSize,
              total: data.total,
              onPageChange: (p) => setFilters({ page: String(p) }),
              onPageSizeChange: (s) => setFilters({ page_size: String(s) }),
            }
          }
        />
      </Card>

      <ContractCreateModal isOpen={creating} onClose={() => setCreating(false)} />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        loading={remove.isPending}
        title={t('Zmazať zmluvu')}
        confirmText={t('Zmazať')}
        message={t('Zmluva {number} ({driver}) a jej dokument budú zmazané. Ak vozidlo ešte nebolo vrátené, zruší sa aj pridelenie vytvorené touto zmluvou. Záznam zostane v audit logu.', { number: deleting?.number, driver: driverName(deleting?.driver) })}
      />
    </>
  );
}
